// Sheet 외부 API + toast primitives.
//
// Sheet의 chrome 자체는 ./Sheet.tsx (Preact)로 이전. 이 파일은 호환 API
// (`openSheet`/`closeSheet`)와 toast/withBusy 같은 부수 헬퍼만 담당한다.
// 시트를 호출하는 수십 곳은 이 모듈만 import 해 왔으니 그대로 유지.

import { $, h, type Child } from "./dom";
import { setSheetState } from "./sheetChrome";

// 버튼을 disabled + spinner 상태로 잠시 바꾸고 비동기 작업이 끝나면 원래
// 라벨로 되돌림. sheet 안 submit 버튼이 거의 다 이 패턴이라 sheet 모듈에 둔다.
export async function withBusy<T>(
  btn: HTMLButtonElement | null,
  busyLabel: string | null,
  fn: () => Promise<T>,
): Promise<T> {
  if (!btn) return await fn();
  const originalHTML = btn.innerHTML;
  const wasDisabled = btn.disabled;
  btn.disabled = true;
  btn.classList.add("btn--busy");
  btn.innerHTML = "";
  const spinner = document.createElement("span");
  spinner.className = "btn__spinner";
  spinner.setAttribute("aria-hidden", "true");
  btn.append(spinner);
  if (busyLabel) btn.append(document.createTextNode(busyLabel));
  try {
    return await fn();
  } finally {
    btn.disabled = wasDisabled;
    btn.classList.remove("btn--busy");
    btn.innerHTML = originalHTML;
  }
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;

export function toast(msg: string): void {
  const el = $("#toast") as HTMLElement | null;
  if (!el) return;
  el.textContent = msg;
  el.hidden = false;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    el.hidden = true;
  }, 2500);
}

export interface OpenSheetOpts {
  // X/ESC/백드롭 dismiss 시 호출자가 정의한 동작으로 오버라이드.
  // multi-step 시트에서 폼의 X를 "리스트로 돌아가기"로 만들 때 사용.
  onClose?: () => void;
}

export function openSheet(
  title: string,
  body: Child,
  footer: Child = null,
  opts: OpenSheetOpts = {},
): void {
  // onClose는 직전 시트에서 새 시트로 넘어갈 때 자동 초기화 — 명시적으로 넘기지
  // 않으면 undefined로 리셋해 leaked dismiss handler 방지.
  setSheetState({ open: true, title, body, footer, onClose: opts.onClose });
}

export function closeSheet(): void {
  setSheetState({ open: false, body: null, footer: null, onClose: undefined });
}

interface MixedInfeasibleDetail {
  team_a_name?: string;
  team_b_name?: string;
  team_a_female_count?: number;
  team_b_female_count?: number;
}

export function openMixedInfeasibleDialog({
  detail,
  onRemoveMixed,
  onAllowCrossTeam,
}: {
  detail: MixedInfeasibleDetail;
  onRemoveMixed?: () => void;
  onAllowCrossTeam?: () => void;
}): void {
  const teamAName = detail.team_a_name || "A팀";
  const teamBName = detail.team_b_name || "B팀";
  const aFemale = detail.team_a_female_count || 0;
  const bFemale = detail.team_b_female_count || 0;
  const emptySide = aFemale === 0 ? teamAName : bFemale === 0 ? teamBName : null;
  const summary = emptySide
    ? `${emptySide}에 여성 참가자가 없어 교류전 방식으로는 혼복(1남+1녀 vs 1남+1녀) 매치를 만들 수 없어요.`
    : "한쪽 팀에 여성 참가자가 없어 혼복 매치를 만들 수 없어요.";

  const body = h("div", { class: "stack" }, [
    h("p", { class: "muted", style: "line-height:1.55;" }, summary),
    h("div", { class: "stack", style: "gap:10px; margin-top:8px;" }, [
      h(
        "button",
        {
          class: "btn btn--block",
          type: "button",
          onclick: () => {
            closeSheet();
            onRemoveMixed?.();
          },
        },
        "혼복 제외하고 생성 (남복만)",
      ),
      h(
        "button",
        {
          class: "btn btn--accent btn--block",
          type: "button",
          onclick: () => {
            closeSheet();
            onAllowCrossTeam?.();
          },
        },
        "혼복은 팀 섞어서 생성",
      ),
      h(
        "div",
        { class: "muted", style: "font-size:12px; line-height:1.5;" },
        "※ '팀 섞어서' 선택 시 혼복 매치는 양 클럽에서 1남+1녀씩 뽑아 진행되며, 교류전 팀 구분은 남복 매치에만 적용돼요.",
      ),
    ]),
  ]);

  const cancelBtn = h(
    "button",
    {
      class: "btn btn--block",
      type: "button",
      onclick: () => closeSheet(),
    },
    "취소",
  );

  openSheet("혼복 구성이 불가능해요", body, cancelBtn);
}
